import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InputGroup from 'react-bootstrap/InputGroup';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { useFormInput, ValidatorType } from 'hooks/useFormInput';
import acApi from 'axios/ac/acApi';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { AccountSettingsDto, AccountSettingsPatchDto } from 'types/rest/accountSettingsTypes';
import { convertToNumber } from 'utils/commonUtil';

const DEFAULT_MIN_HEAP_MB = 512;
const DEFAULT_MAX_HEAP_MB = 16384;

const MemorySettings: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMinHeapMb, setSavedMinHeapMb] = useState<number>(DEFAULT_MIN_HEAP_MB);
  const [savedMaxHeapMb, setSavedMaxHeapMb] = useState<number>(DEFAULT_MAX_HEAP_MB);

  const minHeapInput = useFormInput(
    DEFAULT_MIN_HEAP_MB,
    () => undefined,
    { type: ValidatorType.REQUIRED, invalidMessage: t('memorySettings.minHeapLabel') + ' is required.' },
    { type: ValidatorType.INTEGER, invalidMessage: t('memorySettings.minHeapLabel') + ' must be a whole number.' },
    { type: ValidatorType.MIN, value: 0, invalidMessage: t('memorySettings.minHeapLabel') + ' cannot be negative.' },
  );

  const maxHeapInput = useFormInput(
    DEFAULT_MAX_HEAP_MB,
    () => undefined,
    { type: ValidatorType.REQUIRED, invalidMessage: t('memorySettings.maxHeapLabel') + ' is required.' },
    { type: ValidatorType.INTEGER, invalidMessage: t('memorySettings.maxHeapLabel') + ' must be a whole number.' },
    { type: ValidatorType.MIN, value: 0, invalidMessage: t('memorySettings.maxHeapLabel') + ' cannot be negative.' },
  );

  useEffect(() => {
    let cancelled = false;
    acApi.accountSettings
      .getSettings()
      .then(response => {
        if (cancelled) return;
        const settings: AccountSettingsDto = response.data.result;
        const minHeapMb = settings.minJvmHeapMb ?? DEFAULT_MIN_HEAP_MB;
        const maxHeapMb = settings.maxJvmHeapMb ?? DEFAULT_MAX_HEAP_MB;
        setSavedMinHeapMb(minHeapMb);
        setSavedMaxHeapMb(maxHeapMb);
        minHeapInput.resetValue(minHeapMb);
        maxHeapInput.resetValue(maxHeapMb);
      })
      .catch(() => {
        if (cancelled) return;
        dispatch(showToastNotification(ToastType.Danger, '', t('memorySettings.loadError')));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isDirty =
    convertToNumber(minHeapInput.value) !== savedMinHeapMb || convertToNumber(maxHeapInput.value) !== savedMaxHeapMb;

  const handleCancel = () => {
    minHeapInput.resetValue(savedMinHeapMb);
    maxHeapInput.resetValue(savedMaxHeapMb);
  };

  const handleSave = () => {
    setSaving(true);
    const minJvmHeapMb = convertToNumber(minHeapInput.value);
    const maxJvmHeapMb = convertToNumber(maxHeapInput.value);
    const patch: AccountSettingsPatchDto = { minJvmHeapMb, maxJvmHeapMb };
    acApi.accountSettings
      .patchSettings(patch)
      .then(() => {
        setSavedMinHeapMb(minJvmHeapMb);
        setSavedMaxHeapMb(maxJvmHeapMb);
        dispatch(showToastNotification(ToastType.Success, '', t('memorySettings.saveSuccess')));
      })
      .catch(() => {
        dispatch(showToastNotification(ToastType.Danger, '', t('memorySettings.saveError')));
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <section className="admin-page">
        <h1>{t('pages.memorySettings.title')}</h1>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <h1>{t('pages.memorySettings.title')}</h1>
      <p className="admin-page-subtitle">{t('pages.memorySettings.subtitle')}</p>

      <div className="auto-shutdown-card">
        <div className="auto-shutdown-card-header">
          <span className="auto-shutdown-card-title">{t('memorySettings.cardTitle')}</span>
          <span className="auto-shutdown-account-badge">{t('memorySettings.accountLevel')}</span>
        </div>
        <p className="auto-shutdown-card-description">{t('memorySettings.cardDescription')}</p>

        <div className="auto-shutdown-field-row">
          <span className="auto-shutdown-field-label">{t('memorySettings.minHeapLabel')}</span>
          <InputGroup style={{ width: 200 }}>
            <BzFormControlWithHook id="qa-memory-settings-min-heap" useFormInputHook={minHeapInput} required={true} />
          </InputGroup>
        </div>
        <p className="auto-shutdown-field-hint">{t('memorySettings.minHeapHint')}</p>

        <hr className="auto-shutdown-divider" />

        <div className="auto-shutdown-field-row">
          <span className="auto-shutdown-field-label">{t('memorySettings.maxHeapLabel')}</span>
          <InputGroup style={{ width: 200 }}>
            <BzFormControlWithHook id="qa-memory-settings-max-heap" useFormInputHook={maxHeapInput} required={true} />
          </InputGroup>
        </div>
        <p className="auto-shutdown-field-hint">{t('memorySettings.maxHeapHint')}</p>
      </div>

      <div className="auto-shutdown-footer">
        <button className="btn btn-outline-secondary mr-2" type="button" onClick={handleCancel} disabled={saving}>
          {t('common:cancel')}
        </button>
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty || !minHeapInput.valid || !maxHeapInput.valid}
        >
          {t('memorySettings.saveChanges')}
        </button>
      </div>
    </section>
  );
};

export default MemorySettings;
